import React from "react";

import Image from "./img/blurry-soft-background.jpg";
import { Button, Link, Paper } from "@mui/material";
import { makeStyles } from "./Theme";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { AppPages } from "./App";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

const useStyles = makeStyles()((theme) => ({
  backgroundContainer: {
    width: "100%",
    minHeight: "100vh",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    boxSizing: "border-box",
    // Mobil: weiße Fläche, das Bild lebt im Hero-Header
    background: "#ffffff",
    padding: 0,
    [theme.breakpoints.up("sm")]: {
      background: `url(${Image}) center / cover no-repeat fixed`,
      padding: theme.spacing(4),
      justifyContent: "flex-start",
    },
  },
  heroHeader: {
    position: "relative",
    width: "100%",
    minHeight: 190,
    display: "flex",
    alignItems: "flex-end",
    backgroundImage: `url(${Image})`,
    backgroundSize: "cover",
    backgroundPosition: "center",
    // Platz für das überlappende Sheet + iPhone-Notch
    padding: theme.spacing(3, 2.5, 6),
    paddingTop: "max(env(safe-area-inset-top), 24px)",
    "&::before": {
      content: '""',
      position: "absolute",
      inset: 0,
      background:
        "linear-gradient(180deg, rgba(20, 16, 8, 0.2) 0%, rgba(20, 16, 8, 0.62) 100%)",
    },
    [theme.breakpoints.up("sm")]: {
      display: "none",
    },
  },
  heroTitle: {
    position: "relative",
    color: "#ffffff",
    fontWeight: 800,
    letterSpacing: "-0.02em",
    lineHeight: 1.1,
    fontSize: "clamp(1.6rem, 6.5vw, 2rem)",
    textShadow: "0 2px 12px rgba(0, 0, 0, 0.35)",
  },
  paperContainer: {
    background: "white",
    boxSizing: "border-box",
    [theme.breakpoints.down("sm")]: {
      // Sheet: volle Breite, kein Margin, überlappt den Hero
      width: "100%",
      flexGrow: 1,
      margin: 0,
      marginTop: theme.spacing(-3),
      borderRadius: "20px 20px 0 0",
      padding: theme.spacing(4.5, 2.5, 4),
      boxShadow: "0 -10px 30px rgba(0, 0, 0, 0.12)",
    },
    [theme.breakpoints.up("sm")]: {
      width: "min(760px, 100%)",
      padding: theme.spacing(6),
      borderRadius: theme.spacing(2),
      boxShadow: "0 24px 70px rgba(0, 0, 0, 0.18)",
      marginTop: theme.spacing(2),
      marginBottom: theme.spacing(4),
    },
  },
  title: {
    fontWeight: "bold",
    letterSpacing: "-0.03em",
    lineHeight: 1.08,
  },
  desktopTitleBox: {
    display: "none",
    [theme.breakpoints.up("sm")]: {
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      marginBottom: theme.spacing(1),
    },
  },
  footerBox: {
    width: "fit-content",
    maxWidth: "calc(100% - 32px)",
    borderRadius: 999,
    padding: theme.spacing(1, 3),
    display: "flex",
    background: "rgba(0, 0, 0, 0.72)",
    justifyContent: "center",
    alignItems: "center",
    flexWrap: "wrap",
    margin: theme.spacing(0, "auto", 3),
    [theme.breakpoints.up("sm")]: {
      margin: theme.spacing(0, "auto", 0),
    },
  },
  footerLink: {
    color: "whitesmoke",
    marginLeft: theme.spacing(1),
    marginRight: theme.spacing(1),
  },
}));

export function CarBackgroundBoxLayout(props: {
  title: React.ReactNode;
  setContent: (s: AppPages) => void;
  content?: AppPages;
  children: React.ReactNode;
}) {
  const { classes } = useStyles();
  const { children, title, setContent, content } = props;

  return (
    <Box className={classes.backgroundContainer}>
      <Box className={classes.heroHeader}>
        <Typography component="h1" className={classes.heroTitle}>
          {title}
        </Typography>
      </Box>
      <Paper className={classes.paperContainer} elevation={0}>
        {(content === "Impressum" || content === "Privacy") && (
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => setContent("Form")}
            sx={{ marginBottom: 1, fontWeight: 700 }}
          >
            Zur Startseite
          </Button>
        )}
        <Box className={classes.desktopTitleBox}>
          <Typography
            align="center"
            className={classes.title}
            component="div"
            variant="h4"
          >
            {title}
          </Typography>
        </Box>
        {children}
      </Paper>
      <Box className={classes.footerBox}>
        <Link
          className={classes.footerLink}
          onClick={() => setContent("Impressum")}
        >
          Impressum
        </Link>
        <Link
          className={classes.footerLink}
          onClick={() => setContent("Privacy")}
        >
          Datenschutzerklärung
        </Link>
      </Box>
    </Box>
  );
}
